import { useRef, useState } from "react";
import type { SectionKey, CollapsibleSectionProps } from "../../types/caseView";
import { toTitleCase } from "../../utils/caseViewHelpers";

export function CollapsibleSection({
  sectionKey,
  title,
  icon,
  iconType,
  isExpanded,
  onToggle,
  callMode,
  headerActions,
  children,
  className = '',
  contentClassName = '',
  inContainer = false,
  isLastItem = false,
}: CollapsibleSectionProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  
  const handleHeaderClick = () => {
    onToggle(sectionKey);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(sectionKey);
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const displayTitle = toTitleCase(title);

  // Different styling for in-container vs standalone sections
  if (inContainer) {
    // Inside outline container: flat rows with dividers
    return (
      <div
        id={`section-${sectionKey}`}
        className={`scroll-mt-32 ${className}`}
      >
        {/* Flat Header Row */}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          onClick={handleHeaderClick}
          onKeyDown={handleKeyDown}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={`
            w-full flex items-center justify-between cursor-pointer transition-all duration-150
            focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500/50
            px-4 py-2.5
            ${isExpanded
              ? callMode
                ? "bg-slate-700/40"
                : "bg-slate-50"
              : callMode
                ? "hover:bg-slate-700/20"
                : "hover:bg-slate-50/60"
            }
            ${!isExpanded && !isLastItem
              ? callMode
                ? "border-b border-slate-700/30"
                : "border-b border-slate-100"
              : ""
            }
          `}
        >
          {/* Left: icon + title */}
          <div className="flex items-center gap-2.5">
            {icon && (
              <span className={`flex-shrink-0 transition-all duration-150 ${
                isExpanded ? "opacity-100" : "opacity-40"
              }`} style={{ 
                transform: isExpanded ? 'scale(1)' : 'scale(0.9)',
                transformOrigin: 'center'
              }}>
                {icon}
              </span>
            )}
            <span className={`font-semibold transition-all duration-150 ${
              isExpanded
                ? callMode ? "text-sm text-slate-200" : "text-sm text-slate-700"
                : callMode ? "text-[13px] text-slate-400" : "text-[13px] text-slate-600"
            }`}>
              {displayTitle}
            </span>
          </div>
          
          {/* Right: actions + caret */}
          <div className="flex items-center gap-2">
            {headerActions && (
              <div 
                onClick={handleActionClick} 
                className={`flex items-center gap-1 transition-opacity duration-150 ${
                  isExpanded || isHovered ? "opacity-100" : "opacity-0"
                }`}
              >
                {headerActions}
              </div>
            )}
            <span 
              className={`transition-transform duration-200 select-none ${
                callMode ? "text-slate-500" : "text-slate-400"
              } ${isExpanded ? "rotate-90" : "rotate-0"}`}
              style={{ fontSize: '10px', lineHeight: 1 }}
            >
              ▶
            </span>
          </div>
        </div>
        
        {/* Content */}
        <div
          className={`transition-all duration-200 ease-in-out overflow-hidden ${
            isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div 
            ref={contentRef} 
            className={`
              px-4 py-4
              ${callMode 
                ? "bg-slate-800/30 border-b border-slate-700/30" 
                : "bg-white border-b border-slate-100"
              }
              ${contentClassName}
            `}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Standalone section: card-like with its own container
  return (
    <div
      id={`section-${sectionKey}`}
      className={`scroll-mt-32 rounded-lg overflow-hidden ${
        callMode 
          ? "bg-slate-800/30 border border-slate-700/40" 
          : "bg-white border border-slate-200 shadow-sm"
      } ${className}`}
    >
      {/* Header */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={handleHeaderClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          w-full flex items-center justify-between cursor-pointer transition-all duration-150
          focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500/50
          px-4 py-3
          ${isExpanded
            ? callMode ? "bg-slate-700/30" : "bg-slate-50 border-b border-slate-100"
            : callMode ? "hover:bg-slate-700/20" : "hover:bg-slate-50/60"
          }
        `}
      >
        {/* Left: icon + title */}
        <div className="flex items-center gap-2.5">
          {icon && (
            <span className={`flex-shrink-0 transition-all duration-150 ${
              isExpanded ? "opacity-100" : "opacity-50"
            }`} style={{ 
              transform: isExpanded ? 'scale(1)' : 'scale(0.9)',
              transformOrigin: 'center'
            }}>
              {icon}
            </span>
          )}
          <span className={`font-semibold transition-all duration-150 ${
            isExpanded
              ? callMode ? "text-sm text-slate-200" : "text-sm text-slate-700"
              : callMode ? "text-[13px] text-slate-400" : "text-[13px] text-slate-600"
          }`}>
            {displayTitle}
          </span>
        </div>
        
        {/* Right: actions + caret */}
        <div className="flex items-center gap-2">
          {headerActions && (
            <div 
              onClick={handleActionClick} 
              className={`flex items-center gap-1 transition-opacity duration-150 ${
                isExpanded || isHovered ? "opacity-100" : "opacity-0"
              }`}
            >
              {headerActions}
            </div>
          )}
          <span 
            className={`transition-transform duration-200 select-none ${
              callMode ? "text-slate-500" : "text-slate-400"
            } ${isExpanded ? "rotate-90" : "rotate-0"}`}
            style={{ fontSize: '10px', lineHeight: 1 }}
          >
            ▶
          </span>
        </div>
      </div>
      
      {/* Content */}
      <div
        className={`transition-all duration-200 ease-in-out overflow-hidden ${
          isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div ref={contentRef} className={`px-4 py-4 ${contentClassName}`}>
          {children}
        </div>
      </div>
    </div>
  );
}